import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@trueform/ui/core';

import { CompanyContextService } from '@/core/company-context.service';

/**
 * Global company switcher strip, rendered as a sibling directly above
 * `<tf-header>` in the authenticated shell (ADR-002 Q1 / open question 2).
 * A slim, keyboard-accessible native `<select>` — no custom listbox in this
 * cut. On change it persists the selection via `CompanyContextService.select`
 * and, only when the current URL is under `/catalogs/:companyCode/…`, rewrites
 * the company segment in place; otherwise it persists without navigating.
 *
 * Renders nothing once the lookup settles with exactly ONE company (nothing
 * to pick, and the service already auto-selected it — WUIAM-91); loading,
 * empty and multi-company states keep the bar.
 */
@Component({
  selector: 'app-company-switcher',
  imports: [TranslatePipe],
  template: `
    @if (showBar()) {
      <section
        class="w-full border-b border-border bg-background px-4 py-2"
        aria-label="{{ 'catalogs.company.label' | translate }}"
      >
        <div class="flex items-center gap-3">
          <label for="company-switcher" class="text-xs font-medium text-muted-foreground">
            {{ 'catalogs.company.label' | translate }}
          </label>

          @if (!isReady() && companies().length === 0) {
            <span class="text-xs text-muted-foreground">
              {{ 'catalogs.company.loading' | translate }}
            </span>
          } @else if (companies().length === 0) {
            <span class="text-xs text-muted-foreground">
              {{ 'catalogs.company.empty' | translate }}
            </span>
          } @else {
            <select
              id="company-switcher"
              class="h-8 w-56 rounded-md border border-input bg-transparent px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              [value]="selectedCode() ?? ''"
              (change)="onSelectionChange($any($event.target).value)"
            >
              @if (!selectedCode()) {
                <option value="" disabled>
                  {{ 'catalogs.company.selectPlaceholder' | translate }}
                </option>
              }
              @for (company of companies(); track company.code) {
                <option [value]="company.code">{{ company.name }}</option>
              }
            </select>
          }
        </div>
      </section>
    }
  `,
})
export class CompanySwitcherComponent {
  private readonly companyContext = inject(CompanyContextService);
  private readonly router = inject(Router);

  readonly companies = this.companyContext.companies;
  readonly selectedCode = this.companyContext.selectedCode;
  readonly isReady = this.companyContext.isReady;

  /**
   * With exactly one company there is nothing to pick and
   * `CompanyContextService` has already auto-selected it, so the whole bar is
   * suppressed to avoid a dead full-width strip above the header. Anything
   * else (loading, zero companies, two or more) keeps the bar.
   */
  readonly showBar = computed(() => !(this.isReady() && this.companies().length === 1));

  private readonly catalogsUrl = /^\/catalogs\/([^/]+)(\/.*)?$/;

  constructor() {
    // Kick off the (idempotent) company lookup so the select populates from
    // any screen, not only after the first catalogs navigation.
    this.companyContext.ensureLoaded().pipe(takeUntilDestroyed()).subscribe();
  }

  onSelectionChange(code: string): void {
    if (!code) {
      return;
    }
    this.companyContext.select(code);
    // `router.url` may carry a query string (e.g. `?e2e=true`). Splitting it off
    // keeps the path segments clean for `navigate`, while `queryParamsHandling:
    // 'preserve'` carries the existing query (and e2e activation) forward.
    const path = this.router.url.split('?')[0];
    const match = this.catalogsUrl.exec(path);
    // Only rewrite when the first segment is an actual known company code.
    // Tenant-scoped routes (e.g. `/catalogs/payment-terms`) match the same
    // shape but are NOT company URLs — rewriting them would redirect away to
    // `/catalogs/<code>/products` (REVIEW-001 §1.9 / ADR-002 Q1).
    if (match && this.companyContext.hasCompany(match[1])) {
      const segments = (match[2] ?? '').split('/').filter(Boolean);
      void this.router.navigate(['/catalogs', code, ...segments], {
        queryParamsHandling: 'preserve',
      });
    }
  }
}
